import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, AlertCircle, ChevronLeft, ChevronRight, RefreshCw, Info, CalendarDays, Search, MessageSquare, Copy } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UsageAnalyticsPageHeader } from '@/components/UsageAnalyticsPageHeader';
import { ConversationModeSwitchDialog } from '@/components/ConversationModeSwitchDialog';
import { ThreadUsageModal } from '@/components/ThreadUsageModal';
import { useOpenConversation } from '@/hooks/useOpenConversation';
import { combineCost, useMeteringSummary, useMeteringThreads, useMeteringWarehouse, CostRange } from '@/services/metering.store';
import { showToast } from '@/services/toast.service';

const RANGE_OPTIONS: Array<{ value: CostRange; label: string }> = [
  { value: 'last_24h', label: 'Last 24 hours' },
  { value: 'previous_day', label: 'Previous day' },
  { value: 'previous_7d', label: 'Previous 7 days' },
  { value: 'previous_30d', label: 'Previous 30 days' },
];

const SORT_OPTIONS = [
  { value: 'cost_desc', label: 'Highest cost' },
  { value: 'cost_asc', label: 'Lowest cost' },
  { value: 'tokens_desc', label: 'Most tokens' },
  { value: 'messages_desc', label: 'Most messages' },
  { value: 'recent_desc', label: 'Most recent' },
];

const PAGE_SIZE = 25;

const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }).format(n);

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);

const fmtCompact = (n: number) =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);

const fmtRelative = (iso: string | null): string => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

function StatCard({
  label,
  value,
  hint,
  isLoading,
}: {
  label: string;
  value: string;
  hint?: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-2xl font-semibold">{value}</div>
            {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

const DEFAULT_RANGE: CostRange = 'previous_day';
const DEFAULT_SORT = 'cost_desc';
const VALID_RANGES = new Set<CostRange>([
  'last_24h',
  'previous_day',
  'previous_7d',
  'previous_30d',
]);

// Matches YYYY-MM-DD from a native <input type="date">.
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

// The `to` date in the picker is inclusive of that whole day; the backend
// window is [from, to). Bump `to` by one day so the selected end day is
// included. Pure string math in UTC so it can't drift across DST.
const nextDayISO = (day: string): string => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

export default function Metering() {
  // URL params back the filter state so a refresh / back-button / deep-link
  // restores what the user was looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  // Conversations are branch-scoped, so opening one from here may mean
  // switching the app's mode first — the hook confirms before it does.
  const { open: openConversation, pending, confirm, cancel, switching } = useOpenConversation();
  // The thread breakdown opens over this page so the range / sort / page filters
  // (and the scroll position) survive closing it.
  const [detailThreadId, setDetailThreadId] = useState<string | null>(null);

  const rawRange = searchParams.get('range') as CostRange | null;
  const range: CostRange = rawRange && VALID_RANGES.has(rawRange) ? rawRange : DEFAULT_RANGE;
  const sort = searchParams.get('sort') ?? DEFAULT_SORT;
  const offset = Math.max(0, parseInt(searchParams.get('offset') ?? '0', 10) || 0);
  const search = searchParams.get('search') ?? '';

  // Custom-day window: `from`/`to` are inclusive YYYY-MM-DD in the URL. When
  // both are present and valid they override the preset `range` (the backend
  // also prefers from/to only when range is absent — see _parse_range).
  const rawFrom = searchParams.get('from');
  const rawTo = searchParams.get('to');
  const customDays =
    rawFrom && rawTo && DAY_RE.test(rawFrom) && DAY_RE.test(rawTo) && rawFrom <= rawTo
      ? { from: rawFrom, to: rawTo }
      : null;
  const isCustom = customDays !== null;

  const updateParams = (
    patch: Partial<{ range: CostRange; sort: string; offset: number; from: string | null; to: string | null; search: string }>,
    opts: { replace?: boolean } = {},
  ) => {
    const next = new URLSearchParams(searchParams);
    if (patch.range !== undefined) {
      if (patch.range === DEFAULT_RANGE) next.delete('range');
      else next.set('range', patch.range);
    }
    // Picking a preset range and picking a custom day are mutually exclusive;
    // setting one clears the other so the URL is never ambiguous.
    if (patch.range !== undefined) {
      next.delete('from');
      next.delete('to');
    }
    if (patch.from !== undefined) {
      if (patch.from) next.set('from', patch.from);
      else next.delete('from');
      next.delete('range');
    }
    if (patch.to !== undefined) {
      if (patch.to) next.set('to', patch.to);
      else next.delete('to');
    }
    if (patch.sort !== undefined) {
      if (patch.sort === DEFAULT_SORT) next.delete('sort');
      else next.set('sort', patch.sort);
    }
    if (patch.offset !== undefined) {
      if (patch.offset <= 0) next.delete('offset');
      else next.set('offset', String(patch.offset));
    }
    if (patch.search !== undefined) {
      if (!patch.search) next.delete('search');
      else next.set('search', patch.search);
    }
    setSearchParams(next, { replace: opts.replace ?? false });
  };

  // Backend window: from = start day midnight, to = day-after-end midnight
  // (exclusive). Only sent when a custom day range is active.
  const apiFrom = customDays ? customDays.from : undefined;
  const apiTo = customDays ? nextDayISO(customDays.to) : undefined;

  // Local input state debounces before it hits the URL/query, so typing
  // doesn't refire the request (or push history entries) on every keystroke.
  const [searchInput, setSearchInput] = useState(search);
  const [debouncedSearch] = useDebounce(searchInput, 400);

  useEffect(() => {
    if (debouncedSearch !== search) {
      updateParams({ search: debouncedSearch, offset: 0 }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Keep the input in sync when `search` changes from outside typing (back/
  // forward navigation, a direct link, etc.). Only fires on real URL changes,
  // so it doesn't fight the debounce effect above while the user is typing.
  useEffect(() => {
    setSearchInput(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    isFetching: summaryFetching,
    refetch: refetchSummary,
    dataUpdatedAt: summaryUpdatedAt,
  } = useMeteringSummary({ range, from: apiFrom, to: apiTo });
  const {
    data: warehouse,
    isLoading: warehouseLoading,
    isError: warehouseError,
    isFetching: warehouseFetching,
    refetch: refetchWarehouse,
  } = useMeteringWarehouse({ range, from: apiFrom, to: apiTo });
  // Total / average need both halves; LLM-only cards render as soon as the
  // (fast) LLM summary lands, warehouse cards wait for the Snowflake scan.
  const combined = summary && warehouse ? combineCost(summary, warehouse) : null;
  const totalsLoading = summaryLoading || warehouseLoading;
  const warehouseValue = (render: () => string) =>
    warehouseError ? 'Unavailable' : warehouse ? render() : '—';
  const {
    data: threadsPage,
    isLoading: threadsLoading,
    isError: threadsError,
    isFetching: threadsFetching,
    refetch: refetchThreads,
  } = useMeteringThreads({
    range,
    from: apiFrom,
    to: apiTo,
    limit: PAGE_SIZE,
    offset,
    sort,
    search: search || undefined,
  });

  const isRefreshing = summaryFetching || threadsFetching || warehouseFetching;

  const handleRefresh = () => {
    refetchSummary();
    refetchWarehouse();
    refetchThreads();
  };

  const handleRangeChange = (value: string) => {
    updateParams({ range: value as CostRange, offset: 0 });
  };

  const handleSortChange = (value: string) => {
    updateParams({ sort: value, offset: 0 });
  };

  // Picking either bound activates the custom window. We keep the pair ordered
  // so the range is always valid (from <= to), defaulting the unset side to the
  // one just picked — i.e. picking a single "From" gives a one-day window.
  const handleFromChange = (value: string) => {
    if (!value) return;
    const to = rawTo && rawTo >= value ? rawTo : value;
    updateParams({ from: value, to, offset: 0 });
  };
  const handleToChange = (value: string) => {
    if (!value) return;
    const from = rawFrom && rawFrom <= value ? rawFrom : value;
    updateParams({ from, to: value, offset: 0 });
  };
  const clearCustom = () => {
    updateParams({ range: DEFAULT_RANGE, offset: 0 });
  };

  // Constructed once per render so each thread row's Link captures the
  // currently-active filter state. The detail page uses state.from for its
  // "Back to LLM Metering" link.
  const total = threadsPage?.total_count ?? 0;
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      <UsageAnalyticsPageHeader tab="ellie-usage" />
      <div className="flex-1 overflow-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 -ml-2 text-muted-foreground">
                <Info className="h-4 w-4" />
                About these numbers
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 text-sm space-y-2" align="start">
              <p>
                LLM token usage is captured in real time as the model responds, so the
                LLM cost numbers are accurate up to the second.
              </p>
              <p>
                Warehouse compute is sourced from Snowflake's <code className="font-mono text-xs">ACCOUNT_USAGE.QUERY_ATTRIBUTION_HISTORY</code>,
                which can lag up to 8 hours behind real time. Very recent tool-issued queries
                may not be reflected yet.
              </p>
              <p>
                It also understates real warehouse spend: Snowflake leaves out queries of about
                100&nbsp;ms or less, and doesn't attribute warehouse idle time (the 60-second
                minimum on each resume, plus the time before auto-suspend) to any query.
              </p>
              <p>
                <strong>Previous N</strong> options end at midnight in your tenant's timezone, so partial-day data
                isn't blended in. <strong>Last 24 hours</strong> is a rolling window from now and may show
                incomplete warehouse numbers for the most recent few hours.
              </p>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          {summaryUpdatedAt ? (
            <span className="text-xs text-muted-foreground" title={new Date(summaryUpdatedAt).toLocaleString()}>
              Updated {fmtRelative(new Date(summaryUpdatedAt).toISOString())}
            </span>
          ) : null}
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
          <Select value={isCustom ? '' : range} onValueChange={handleRangeChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Custom range" />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={customDays ? 'default' : 'outline'}
                size="sm"
                className="gap-1.5"
                title="Pick a specific day or date range"
              >
                <CalendarDays className="h-4 w-4" />
                {customDays
                  ? customDays.from === customDays.to
                    ? customDays.from
                    : `${customDays.from} → ${customDays.to}`
                  : 'Custom range'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-3" align="end">
              <div className="text-sm font-medium">
                Custom range ({summary?.timezone ?? 'UTC'})
              </div>
              <div className="space-y-2">
                <label className="block text-xs text-muted-foreground">
                  From
                  <Input
                    type="date"
                    value={rawFrom ?? ''}
                    max={rawTo ?? undefined}
                    onChange={(e) => handleFromChange(e.target.value)}
                    className="mt-1"
                  />
                </label>
                <label className="block text-xs text-muted-foreground">
                  To
                  <Input
                    type="date"
                    value={rawTo ?? ''}
                    min={rawFrom ?? undefined}
                    onChange={(e) => handleToChange(e.target.value)}
                    className="mt-1"
                  />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Both ends are inclusive. Pick one day for a single-day view. Days
                are in your workspace timezone ({summary?.timezone ?? 'UTC'}).
              </p>
              {isCustom ? (
                <Button variant="ghost" size="sm" className="w-full" onClick={clearCustom}>
                  Clear
                </Button>
              ) : null}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {summary?.unrated_models && summary.unrated_models.length > 0 ? (
        <div className="border rounded-md p-3 mb-4 text-sm flex items-start gap-2 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-900 text-yellow-900 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">
              {summary.unrated_models.length === 1 ? 'A model is missing a rate card' : 'Models are missing rate cards'}
              {' — '}those turns are pricing as $0.
            </div>
            <div className="mt-1">
              <span className="font-mono text-xs">{summary.unrated_models.join(', ')}</span>
              <br />
              Add to <code className="font-mono text-xs">LATEST_RATE_CARDS</code> in{' '}
              <code className="font-mono text-xs">src/services/metering/service.py</code> and run{' '}
              <code className="font-mono text-xs">apply_latest_rate_cards()</code> from <code className="font-mono text-xs">flask_shell</code>.
            </div>
          </div>
        </div>
      ) : null}

      {warehouse?.warehouse_unavailable ? (
        <div className="border rounded-md p-4 mb-6 text-sm flex items-start gap-2 bg-yellow-50 dark:bg-yellow-950/30 border-yellow-300 dark:border-yellow-900 text-yellow-900 dark:text-yellow-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Warehouse cost attribution is unavailable.</div>
            <div className="mt-1">
              Your Snowflake role can't read <code className="font-mono">SNOWFLAKE.ACCOUNT_USAGE</code>.
              The LLM-token cost below is accurate; warehouse compute is reported as $0.
            </div>
            <div className="mt-2">
              Run this as <code className="font-mono">ACCOUNTADMIN</code> to enable it:
              <pre className="mt-1 p-2 bg-yellow-100/60 dark:bg-yellow-900/40 rounded font-mono text-xs overflow-x-auto">
{`GRANT IMPORTED PRIVILEGES ON DATABASE SNOWFLAKE TO ROLE ${warehouse.warehouse_role ?? '<your_role>'};`}
              </pre>
            </div>
          </div>
        </div>
      ) : null}

      {summaryError ? (
        <div className="border rounded-md p-4 mb-6 text-sm flex items-center gap-2 text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load cost summary.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard
              label="Total threads"
              value={summary ? fmtNum(summary.thread_count) : '—'}
              isLoading={summaryLoading}
            />
            <StatCard
              label="Total tokens"
              value={summary ? fmtCompact(summary.total_tokens) : '—'}
              hint={
                summary
                  ? `${fmtCompact(summary.input_tokens)} in · ${fmtCompact(
                      summary.output_tokens,
                    )} out · ${fmtCompact(
                      summary.cache_creation_input_tokens,
                    )} cache write · ${fmtCompact(summary.cache_read_input_tokens)} cache read`
                  : undefined
              }
              isLoading={summaryLoading}
            />
            <StatCard
              label="Total cost"
              value={
                combined
                  ? fmtUsd(combined.totalCostUsd)
                  : warehouseError && summary
                    ? fmtUsd(summary.llm_cost_usd)
                    : '—'
              }
              hint={warehouseError && summary ? 'LLM only — warehouse cost failed to load' : undefined}
              isLoading={summaryLoading || (warehouseLoading && !warehouseError)}
            />
            <StatCard
              label="Avg cost / thread"
              value={combined ? fmtUsd(combined.avgCostPerThreadUsd) : '—'}
              isLoading={totalsLoading && !warehouseError}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <StatCard
              label="LLM token cost"
              value={summary ? fmtUsd(summary.llm_cost_usd) : '—'}
              hint={
                summary
                  ? `${fmtUsd(summary.input_cost_usd)} input · ${fmtUsd(
                      summary.output_cost_usd,
                    )} output · ${fmtUsd(
                      summary.cache_creation_cost_usd,
                    )} cache write · ${fmtUsd(summary.cache_read_cost_usd)} cache read`
                  : undefined
              }
              isLoading={summaryLoading}
            />
            <StatCard
              label="Warehouse compute cost"
              value={warehouseValue(() => fmtUsd(warehouse!.warehouse_cost_usd))}
              hint={
                warehouse
                  ? `${fmtNum(warehouse.warehouse_query_count)} queries · ${warehouse.warehouse_credits.toFixed(
                      4,
                    )} credits`
                  : undefined
              }
              isLoading={warehouseLoading}
            />
            <StatCard
              label="Background jobs"
              value={summary ? fmtUsd(summary.background_cost_usd ?? 0) : '—'}
              hint={
                summary && summary.background_by_source?.length
                  ? summary.background_by_source
                      .map((s) => `${s.source} ${fmtUsd(s.total_cost_usd)}`)
                      .join(' · ')
                  : 'LLM spend not tied to a chat thread (consolidation, column suggest, …)'
              }
              isLoading={summaryLoading}
            />
          </div>

          {/* Snowflake credits — shown separately from the USD totals so
              operators can sanity-check the dashboard against METERING_HISTORY
              / Snowsight Cost Management, both of which report credits, not
              dollars. The conversion to dollars depends on the tenant's
              contracted $/credit and is set in TenantConfig.cost. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <StatCard
              label="LLM credits"
              value={summary ? summary.llm_credits.toFixed(4) : '—'}
              hint={
                summary
                  ? `${summary.input_credits.toFixed(2)} input · ${summary.output_credits.toFixed(2)} output · ${summary.cache_creation_credits.toFixed(2)} cache write · ${summary.cache_read_credits.toFixed(2)} cache read`
                  : 'Snowflake credits consumed via Cortex'
              }
              isLoading={summaryLoading}
            />
            <StatCard
              label="Warehouse credits"
              value={warehouseValue(() => warehouse!.warehouse_credits.toFixed(4))}
              hint={
                warehouse
                  ? `${fmtNum(warehouse.warehouse_query_count)} attributed queries${
                      warehouse.warehouse_qas_credits
                        ? ` · ${warehouse.warehouse_qas_credits.toFixed(4)} query acceleration`
                        : ''
                    }`
                  : 'Snowflake compute credits via QUERY_ATTRIBUTION_HISTORY'
              }
              isLoading={warehouseLoading}
            />
          </div>

          <p className="flex items-start gap-2 text-xs text-muted-foreground mb-8">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Warehouse cost counts only the queries Ellie runs, and it reads low. Snowflake reports it up
              to 8 hours late, leaves out queries of about 100&nbsp;ms or less, and doesn't count the time
              a warehouse sits idle before suspending. Use Snowsight Cost Management for your full
              warehouse bill.
            </span>
          </p>
        </>
      )}

      <div className="flex items-center justify-between mb-3 gap-3">
        <h2 className="text-lg font-semibold">Top threads</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search email, user id, or thread id"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-8 w-80"
            />
          </div>
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-md min-h-[560px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Thread</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Last activity</TableHead>
              <TableHead className="text-right">Messages</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">LLM cost</TableHead>
              <TableHead className="text-right">Warehouse cost</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {threadsLoading ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Loading threads…
                </TableCell>
              </TableRow>
            ) : threadsError ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-destructive py-8">
                  Failed to load threads.
                </TableCell>
              </TableRow>
            ) : !threadsPage || threadsPage.threads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  {search ? 'No threads match your search.' : 'No threads with usage in this window yet.'}
                </TableCell>
              </TableRow>
            ) : (
              threadsPage.threads.map((t) => {
                return (
                  <TableRow key={t.thread_id}>
                  <TableCell className="font-mono text-xs">
                    <button
                      type="button"
                      onClick={() => setDetailThreadId(t.thread_id)}
                      className="hover:underline"
                      title="View usage detail"
                    >
                      {t.thread_id.slice(0, 12)}…
                    </button>
                    {t.models.length > 0 ? (
                      <div className="mt-1 flex gap-1">
                        {t.models.map((m) => (
                          <Badge key={m} variant="secondary" className="text-[10px]">
                            {m}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">
                    <button
                      type="button"
                      onClick={() => openConversation(t.thread_id, { branch: t.branch, agentType: t.agent_type })}
                      className="flex items-center gap-1.5 hover:opacity-80"
                      title="Open chat thread"
                    >
                      {t.agent_type ? (
                        <Badge variant="outline" className="text-[10px]">{t.agent_type}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{t.actor_label ?? <span className="text-muted-foreground">—</span>}</div>
                    {t.actor_id ? (
                      <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                        <span className="truncate max-w-[140px]" title={t.actor_id}>
                          {t.actor_id}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(t.actor_id!);
                            showToast.success('User ID copied');
                          }}
                          className="flex-shrink-0 hover:text-foreground"
                          title="Copy user ID"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-sm">
                    {t.branch ? (
                      <Badge variant="outline" className="text-[10px]">{t.branch}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    <span title={t.last_message_at ?? ''}>{fmtRelative(t.last_message_at)}</span>
                  </TableCell>
                  <TableCell className="text-right">{fmtNum(t.message_count)}</TableCell>
                  <TableCell className="text-right">{fmtCompact(t.total_tokens)}</TableCell>
                  <TableCell className="text-right">{fmtUsd(t.llm_cost_usd)}</TableCell>
                  <TableCell className="text-right">{fmtUsd(t.warehouse_cost_usd)}</TableCell>
                  <TableCell className="text-right font-medium">{fmtUsd(t.total_cost_usd)}</TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-3 text-sm text-muted-foreground">
        <div>
          {total === 0
            ? '0 threads'
            : `Showing ${fmtNum(start)}–${fmtNum(end)} of ${fmtNum(total)}`}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={!canPrev}
            onClick={() => updateParams({ offset: Math.max(0, offset - PAGE_SIZE) })}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!canNext}
            onClick={() => updateParams({ offset: offset + PAGE_SIZE })}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
      </div>

      <ConversationModeSwitchDialog
        pending={pending}
        onConfirm={confirm}
        onCancel={cancel}
        switching={switching}
      />

      <ThreadUsageModal threadId={detailThreadId} onClose={() => setDetailThreadId(null)} />
    </div>
  );
}
